import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Tabs,
  Tab,
  Container,
  Button,
  TextField,
  Grid,
  Card,
  CardContent,
  CardActions,
  Alert,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  CircularProgress,
  Divider,
} from '@mui/material';
import {
  CloudDownload,
  Sync,
  Assessment,
  Warning,
  CheckCircle,
  Error as ErrorIcon,
  Storage,
  Search,
  CompareArrows,
} from '@mui/icons-material';
import { useNotification } from '../components/NotificationContext';
import axios from 'axios';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`nimbus-tabpanel-${index}`}
      aria-labelledby={`nimbus-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const Nimbus: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState<{[key: string]: boolean}>({});
  const { showNotification } = useNotification();
  
  // API parameters state
  const [incremental, setIncremental] = useState(true);
  const [format, setFormat] = useState('excel');
  const [discrepancyType, setDiscrepancyType] = useState('all');
  const [limit, setLimit] = useState('100');
  const [offset, setOffset] = useState('0');
  const [testSystem, setTestSystem] = useState('all');
  const [itemId, setItemId] = useState('');
  
  const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://finops-portal.angel-admin.angelstudios.com';

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleApiCall = async (apiKey: string, endpoint: string, method: string = 'GET', data?: any, isFileDownload: boolean = false) => {
    setLoading(prev => ({ ...prev, [apiKey]: true }));
    try {
      const response = await axios({
        method,
        url: `${API_BASE_URL}${endpoint}`,
        data,
        withCredentials: true,
        ...(isFileDownload && { responseType: 'blob' })
      });
      
      if (isFileDownload) {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `inventory-report-${new Date().toISOString().split('T')[0]}.xlsx`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        showNotification('Report downloaded successfully!', 'success');
      } else {
        showNotification('Request completed successfully!', 'success');
        console.log('API Response:', response.data);
      }
      
      return response.data;
    } catch (error: any) {
      console.error('=== NIMBUS API Error Details ===');
      console.error('Endpoint:', endpoint);
      console.error('Method:', method);
      console.error('Full Error Object:', error);
      console.error('Error Message:', error.message);
      console.error('Error Code:', error.code);
      console.error('Response Status:', error.response?.status);
      console.error('Response Data:', error.response?.data);
      console.error('Response Headers:', error.response?.headers);
      console.error('Request URL:', error.config?.url);
      console.error('================================');
      
      if (error.response?.status === 404) {
        showNotification('API endpoint not found', 'error');
      } else {
        showNotification(error.response?.data?.error || error.message || 'API call failed', 'error');
      }
    } finally {
      setLoading(prev => ({ ...prev, [apiKey]: false }));
    }
  };

  return (
    <Container maxWidth="xl">
      <Paper elevation={3} sx={{ p: 4, mb: 4 }}>
        <Typography 
          variant="h4" 
          component="h1" 
          gutterBottom
          sx={{ fontWeight: 600, mb: 3 }}
        >
          NetSuite-First Integration & Modular Business Update Service
        </Typography>

        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs 
            value={activeTab} 
            onChange={handleTabChange}
            aria-label="NIMBUS tabs"
            sx={{
              '& .MuiTab-root': {
                textTransform: 'none',
                fontSize: '1rem',
                fontWeight: 500,
              }
            }}
          >
            <Tab label="Inventory" id="nimbus-tab-0" />
            <Tab label="Module 2" id="nimbus-tab-1" />
            <Tab label="Module 3" id="nimbus-tab-2" />
          </Tabs>
        </Box>

        {/* INVENTORY TAB */}
        <TabPanel value={activeTab} index={0}>
          <Alert severity="success" sx={{ mb: 3 }}>
            <Typography variant="body2">
              <strong>NIMBUS API Connected:</strong> Inventory reconciliation endpoints are now live and operational.
            </Typography>
          </Alert>

          <Grid container spacing={3}>
            {/* API #1: Generate Full Report */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Card elevation={2}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Assessment sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography variant="h6">Generate Full Report</Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Generate and stream full inventory comparison report directly
                  </Typography>
                  <Divider sx={{ my: 2 }} />
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Reports are now streamed directly - no need to download separately
                  </Alert>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={incremental}
                        onChange={(e) => setIncremental(e.target.checked)}
                      />
                    }
                    label="Incremental Sync"
                  />
                  <FormControl fullWidth sx={{ mt: 2 }}>
                    <InputLabel>Format</InputLabel>
                    <Select value={format} onChange={(e) => setFormat(e.target.value)} label="Format">
                      <MenuItem value="excel">Excel</MenuItem>
                      <MenuItem value="csv">CSV (Future)</MenuItem>
                      <MenuItem value="json">JSON (Future)</MenuItem>
                    </Select>
                  </FormControl>
                </CardContent>
                <CardActions>
                  <Button
                    variant="contained"
                    startIcon={loading.generateReport ? <CircularProgress size={20} /> : <CloudDownload />}
                    disabled={loading.generateReport}
                    onClick={() => handleApiCall('generateReport', `/api/nimbus/inventory/reconciliation/generate?incremental=${incremental}&format=${format}`, 'POST', undefined, true)}
                    fullWidth
                  >
                    Generate Report
                  </Button>
                </CardActions>
              </Card>
            </Grid>

            {/* API #2: Get Summary */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Card elevation={2}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Storage sx={{ mr: 1, color: 'success.main' }} />
                    <Typography variant="h6">Summary Dashboard</Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Get summary statistics without generating full report
                  </Typography>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="caption" color="text.secondary">
                    Use case: Dashboard widgets, quick health check
                  </Typography>
                </CardContent>
                <CardActions>
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={loading.summary ? <CircularProgress size={20} /> : <Assessment />}
                    disabled={loading.summary}
                    onClick={() => handleApiCall('summary', '/api/nimbus/inventory/reconciliation/summary')}
                    fullWidth
                  >
                    View Summary
                  </Button>
                </CardActions>
              </Card>
            </Grid>

            {/* API #3: Get Discrepancies */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Card elevation={2}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <CompareArrows sx={{ mr: 1, color: 'warning.main' }} />
                    <Typography variant="h6">View Discrepancies</Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Get list of items with quantity mismatches
                  </Typography>
                  <Divider sx={{ my: 2 }} />
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>Type</InputLabel>
                    <Select value={discrepancyType} onChange={(e) => setDiscrepancyType(e.target.value)} label="Type">
                      <MenuItem value="all">All</MenuItem>
                      <MenuItem value="shopify_higher">Shopify Higher</MenuItem>
                      <MenuItem value="netsuite_higher">NetSuite Higher</MenuItem>
                    </Select>
                  </FormControl>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 6 }}>
                      <TextField
                        fullWidth
                        label="Limit"
                        type="number"
                        value={limit}
                        onChange={(e) => setLimit(e.target.value)}
                        size="small"
                      />
                    </Grid>
                    <Grid size={{ xs: 6 }}>
                      <TextField
                        fullWidth
                        label="Offset"
                        type="number"
                        value={offset}
                        onChange={(e) => setOffset(e.target.value)}
                        size="small"
                      />
                    </Grid>
                  </Grid>
                </CardContent>
                <CardActions>
                  <Button
                    variant="contained"
                    color="warning"
                    startIcon={loading.discrepancies ? <CircularProgress size={20} /> : <Warning />}
                    disabled={loading.discrepancies}
                    onClick={() => handleApiCall('discrepancies', `/api/nimbus/inventory/discrepancies?type=${discrepancyType}&limit=${limit}&offset=${offset}`)}
                    fullWidth
                  >
                    View Discrepancies
                  </Button>
                </CardActions>
              </Card>
            </Grid>

            {/* API #4: Missing Mapping */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Card elevation={2}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <ErrorIcon sx={{ mr: 1, color: 'error.main' }} />
                    <Typography variant="h6">Missing Shopify Mapping</Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Get NetSuite items without Shopify webstore mapping
                  </Typography>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="caption" color="text.secondary">
                    Use case: Data quality monitoring, setup issues
                  </Typography>
                </CardContent>
                <CardActions>
                  <Button
                    variant="contained"
                    color="error"
                    startIcon={loading.missingMapping ? <CircularProgress size={20} /> : <ErrorIcon />}
                    disabled={loading.missingMapping}
                    onClick={() => handleApiCall('missingMapping', '/api/nimbus/inventory/missing-mapping')}
                    fullWidth
                  >
                    View Missing Mappings
                  </Button>
                </CardActions>
              </Card>
            </Grid>

            {/* API #5: Shopify Orphans */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Card elevation={2}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Search sx={{ mr: 1, color: 'secondary.main' }} />
                    <Typography variant="h6">Shopify Orphans</Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Get Shopify items NOT found in NetSuite
                  </Typography>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="caption" color="text.secondary">
                    Use case: Identify SKUs in Shopify that should be in NetSuite
                  </Typography>
                </CardContent>
                <CardActions>
                  <Button
                    variant="contained"
                    color="secondary"
                    startIcon={loading.orphans ? <CircularProgress size={20} /> : <Search />}
                    disabled={loading.orphans}
                    onClick={() => handleApiCall('orphans', '/api/nimbus/inventory/shopify-orphans')}
                    fullWidth
                  >
                    View Shopify Orphans
                  </Button>
                </CardActions>
              </Card>
            </Grid>

            {/* API #10: Item Lookup */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Card elevation={2}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Search sx={{ mr: 1, color: 'info.main' }} />
                    <Typography variant="h6">Item Lookup</Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Get reconciliation details for specific NetSuite item
                  </Typography>
                  <Divider sx={{ my: 2 }} />
                  <TextField
                    fullWidth
                    label="NetSuite Item ID"
                    value={itemId}
                    onChange={(e) => setItemId(e.target.value)}
                    placeholder="12345"
                  />
                </CardContent>
                <CardActions>
                  <Button
                    variant="contained"
                    color="info"
                    startIcon={loading.itemLookup ? <CircularProgress size={20} /> : <Search />}
                    disabled={loading.itemLookup || !itemId}
                    onClick={() => handleApiCall('itemLookup', `/api/nimbus/inventory/item/${itemId}`)}
                    fullWidth
                  >
                    Lookup Item
                  </Button>
                </CardActions>
              </Card>
            </Grid>

            {/* Sync Management Section */}
            <Grid size={{ xs: 12 }}>
              <Typography variant="h5" sx={{ mt: 3, mb: 2, fontWeight: 600 }}>
                Sync Management
              </Typography>
              <Divider />
            </Grid>

            {/* API #6: Trigger Sync */}
            <Grid size={{ xs: 12, md: 4 }}>
              <Card elevation={2}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Sync sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography variant="h6">Trigger Sync</Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Manually trigger a sync
                  </Typography>
                  <Divider sx={{ my: 2 }} />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={incremental}
                        onChange={(e) => setIncremental(e.target.checked)}
                      />
                    }
                    label="Incremental Sync"
                  />
                </CardContent>
                <CardActions>
                  <Button
                    variant="contained"
                    startIcon={loading.triggerSync ? <CircularProgress size={20} /> : <Sync />}
                    disabled={loading.triggerSync}
                    onClick={() => handleApiCall('triggerSync', '/api/nimbus/sync/trigger', 'POST', { incremental, systems: ['netsuite', 'shopify'] })}
                    fullWidth
                  >
                    Trigger Sync Now
                  </Button>
                </CardActions>
              </Card>
            </Grid>

            {/* API #7: Sync Status */}
            <Grid size={{ xs: 12, md: 4 }}>
              <Card elevation={2}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <CheckCircle sx={{ mr: 1, color: 'success.main' }} />
                    <Typography variant="h6">Check Sync Status</Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Check sync status
                  </Typography>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="caption" color="text.secondary">
                    View last sync times and next scheduled sync
                  </Typography>
                </CardContent>
                <CardActions>
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={loading.syncStatus ? <CircularProgress size={20} /> : <CheckCircle />}
                    disabled={loading.syncStatus}
                    onClick={() => handleApiCall('syncStatus', '/api/nimbus/sync/status')}
                    fullWidth
                  >
                    Check Status
                  </Button>
                </CardActions>
              </Card>
            </Grid>

            {/* API #9: Test Connections */}
            <Grid size={{ xs: 12, md: 4 }}>
              <Card elevation={2}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <CheckCircle sx={{ mr: 1, color: 'info.main' }} />
                    <Typography variant="h6">Test Connections</Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Test connector connections
                  </Typography>
                  <Divider sx={{ my: 2 }} />
                  <FormControl fullWidth>
                    <InputLabel>System</InputLabel>
                    <Select value={testSystem} onChange={(e) => setTestSystem(e.target.value)} label="System">
                      <MenuItem value="all">All Systems</MenuItem>
                      <MenuItem value="netsuite">NetSuite</MenuItem>
                      <MenuItem value="shopify">Shopify</MenuItem>
                    </Select>
                  </FormControl>
                </CardContent>
                <CardActions>
                  <Button
                    variant="contained"
                    color="info"
                    startIcon={loading.testConnection ? <CircularProgress size={20} /> : <CheckCircle />}
                    disabled={loading.testConnection}
                    onClick={() => handleApiCall('testConnection', '/api/nimbus/connections/test', 'POST', { system: testSystem })}
                    fullWidth
                  >
                    Test Connections
                  </Button>
                </CardActions>
              </Card>
            </Grid>

            {/* System Health Section */}
            <Grid size={{ xs: 12 }}>
              <Typography variant="h5" sx={{ mt: 3, mb: 2, fontWeight: 600 }}>
                System Health
              </Typography>
              <Divider />
            </Grid>

            {/* API #8: Health Check */}
            <Grid size={{ xs: 12, md: 12 }}>
              <Card elevation={2}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <CheckCircle sx={{ mr: 1, color: 'success.main' }} />
                    <Typography variant="h6">System Health</Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    System health check
                  </Typography>
                  <Divider sx={{ my: 2 }} />
                  <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                    <Chip label="Last Sync: Not Available" size="small" />
                    <Chip label="Status: Unknown" color="default" size="small" />
                  </Box>
                </CardContent>
                <CardActions>
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={loading.health ? <CircularProgress size={20} /> : <CheckCircle />}
                    disabled={loading.health}
                    onClick={() => handleApiCall('health', '/api/nimbus/health')}
                    fullWidth
                  >
                    Check System Health
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        {/* MODULE 2 TAB */}
        <TabPanel value={activeTab} index={1}>
          <Typography variant="h6" gutterBottom>
            Module 2
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Content for Module 2 will be added here.
          </Typography>
        </TabPanel>

        {/* MODULE 3 TAB */}
        <TabPanel value={activeTab} index={2}>
          <Typography variant="h6" gutterBottom>
            Module 3
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Content for Module 3 will be added here.
          </Typography>
        </TabPanel>
      </Paper>
    </Container>
  );
};

export default Nimbus;
